/**
 * photo_viewer_controller.js — enlarges a photo full screen.
 *
 * Tapping a photo opens a native <dialog> on the full-size copy, with a close
 * button and, for a set of photos, previous/next buttons, arrow keys and a
 * horizontal swipe. Escape and a tap on the backdrop close it (the browser
 * handles Escape for a modal dialog).
 *
 * Opening pushes a history entry, so the browser's or phone's Back closes the
 * photo and leaves the page beneath as it was (an open support chat stays
 * open). Every close goes through the dialog's `close` event, which consumes
 * that entry unless Back already did.
 *
 * Two ways to open:
 *   index param  one of the photos in the urls value (a fixed grid)
 *   url param    that one photo (photos rendered after load, as in a chat)
 *
 * Values: urls — the full-size image URLs, in grid order (grid use only).
 * Templates: templates/directory/listing.html, templates/components/support_thread.html.
 * CSS: static/css/components/photo_viewer.css
 */

import { Controller } from "@hotwired/stimulus";

const SWIPE_PX = 40;
const HISTORY_KEY = 'chippiePhotoViewer';

export default class extends Controller {
    static targets = ['dialog', 'image', 'count'];
    static values = { urls: Array };

    connect() {
        this._index = 0;
        this._urls = [];
        this._onTouchStart = (event) => { this._touchX = event.touches[0].clientX; };
        this._onTouchEnd = (event) => this._swipe(event.changedTouches[0].clientX - this._touchX);
        this._onClose = () => this._consumeHistoryEntry();
        this._onPopState = () => { if (this.dialogTarget.open) this.dialogTarget.close(); };
        this.dialogTarget.addEventListener('touchstart', this._onTouchStart, { passive: true });
        this.dialogTarget.addEventListener('touchend', this._onTouchEnd);
        this.dialogTarget.addEventListener('close', this._onClose);
        window.addEventListener('popstate', this._onPopState);
    }

    disconnect() {
        this.dialogTarget.removeEventListener('touchstart', this._onTouchStart);
        this.dialogTarget.removeEventListener('touchend', this._onTouchEnd);
        this.dialogTarget.removeEventListener('close', this._onClose);
        window.removeEventListener('popstate', this._onPopState);
        if (this.dialogTarget.open) this.dialogTarget.close();
    }

    open({ params: { index, url } }) {
        this._urls = url ? [url] : this.urlsValue;
        this._show(url ? 0 : index);
        this.dialogTarget.showModal();
        if (!this._historyEntryActive()) history.pushState({ [HISTORY_KEY]: true }, '');
    }

    close() {
        this.dialogTarget.close();
    }

    next() {
        this._show(this._index + 1);
    }

    prev() {
        this._show(this._index - 1);
    }

    /** A tap on the dark area around the photo, not on the photo or a button. */
    backdrop(event) {
        if (event.target === this.dialogTarget) this.close();
    }

    key(event) {
        if (event.key === 'ArrowRight') this.next();
        if (event.key === 'ArrowLeft') this.prev();
    }

    _historyEntryActive() {
        return !!(history.state && history.state[HISTORY_KEY]);
    }

    // A close by the button, backdrop or Escape leaves the entry open() pushed;
    // stepping back over it keeps Back from landing on a closed viewer. A close
    // caused by Back finds the entry already gone.
    _consumeHistoryEntry() {
        if (this._historyEntryActive()) history.back();
    }

    _swipe(dx) {
        if (this._urls.length < 2 || Math.abs(dx) < SWIPE_PX) return;
        if (dx < 0) this.next();
        else this.prev();
    }

    _show(index) {
        const total = this._urls.length;
        this._index = (index + total) % total;
        this.imageTarget.src = this._urls[this._index];
        if (this.hasCountTarget) this.countTarget.textContent = `${this._index + 1} / ${total}`;
    }
}
